import React, { useState, useEffect } from 'react';
import API from '../lib/api';
import { Users, FileText, MessageSquare, Clock, Plus, ArrowUpRight, TrendingUp, BarChart3, AlertTriangle, ClipboardList } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

export default function CompanyDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ workers: 0, documents: 0, communications: 0, today_records: 0 });
  const [recentRecords, setRecentRecords] = useState([]);
  const [weeklyData, setWeeklyData] = useState([]);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, recordsRes, notifRes] = await Promise.all([
        API.get('/stats/company'),
        API.get('/time-records').catch(() => ({ data: [] })),
        API.get('/notifications/unread-count').catch(() => ({ data: { count: 0 } }))
      ]);
      setStats(statsRes.data);
      setRecentRecords(Array.isArray(recordsRes.data) ? recordsRes.data.slice(0, 5) : []);
      setUnreadMessages(notifRes.data.count || 0);

      // Generate weekly chart data from records
      const now = new Date();
      const days = [];
      for (let i = 6; i >= 0; i--) {
        const d = new Date(now);
        d.setDate(d.getDate() - i);
        const dateStr = d.toISOString().split('T')[0];
        const dayRecords = (Array.isArray(recordsRes.data) ? recordsRes.data : []).filter(r => 
          r.clock_in_time?.startsWith(dateStr)
        );
        days.push({
          day: d.toLocaleDateString('es-ES', { weekday: 'short' }),
          fichajes: dayRecords.length
        });
      }
      setWeeklyData(days);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight text-gray-900">
            Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {user?.company_name || 'Tu empresa'} — Vista general
          </p>
        </div>
        <Button
          onClick={() => navigate('/company/workers')}
          className="bg-[#FF6B35] hover:bg-[#E55A2B] text-white rounded-lg shadow-sm"
          data-testid="add-worker-quick-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Trabajador
        </Button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Trabajadores"
          value={stats.workers}
          onClick={() => navigate('/company/workers')}
          accent="bg-orange-50 text-[#FF6B35]"
        />
        <StatCard
          icon={FileText}
          label="Documentos"
          value={stats.documents}
          onClick={() => navigate('/company/documents')}
          accent="bg-blue-50 text-blue-600"
        />
        <StatCard
          icon={MessageSquare}
          label="Comunicados"
          value={stats.communications}
          onClick={() => navigate('/company/communications')}
          accent="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          icon={Clock}
          label="Fichajes hoy"
          value={stats.today_records}
          onClick={() => navigate('/company/time-records')}
          accent="bg-amber-50 text-amber-600"
        />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        {/* Weekly Activity Chart */}
        <div className="lg:col-span-8 bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-lg font-medium text-gray-900">Actividad semanal</h2>
              <p className="text-xs text-gray-500">Fichajes de los ultimos 7 dias</p>
            </div>
            <div className="flex items-center gap-1 text-xs font-medium text-[#FF6B35] bg-orange-50 px-2.5 py-1 rounded-full">
              <TrendingUp className="w-3 h-3" />
              {stats.today_records} hoy
            </div>
          </div>
          <div className="h-48" style={{ minHeight: 192 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={weeklyData} margin={{ top: 5, right: 5, bottom: 0, left: -20 }}>
                <defs>
                  <linearGradient id="orangeGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#FF6B35" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: '#9CA3AF' }} />
                <Tooltip
                  contentStyle={{ borderRadius: '8px', border: '1px solid #E5E7EB', fontSize: '12px' }}
                  labelStyle={{ fontWeight: 600 }}
                />
                <Area type="monotone" dataKey="fichajes" stroke="#FF6B35" strokeWidth={2.5} fill="url(#orangeGradient)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="lg:col-span-4 space-y-3">
          <h2 className="text-xs font-semibold tracking-widest text-gray-500 uppercase px-1">Acciones rapidas</h2>
          <QuickAction
            icon={MessageSquare}
            label="Enviar Comunicado"
            sublabel="Informar a los trabajadores"
            onClick={() => navigate('/company/communications')}
          />
          <QuickAction
            icon={BarChart3}
            label="Ver Estadisticas"
            sublabel="Informes y metricas"
            onClick={() => navigate('/company/analytics')}
          />
          <QuickAction
            icon={ClipboardList}
            label="Partes Trabajo"
            sublabel="Revisar partes de turno"
            onClick={() => navigate('/company/work-reports')}
          />
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm">
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-medium text-gray-900">Actividad reciente</h2>
            <p className="text-xs text-gray-500">Ultimos fichajes registrados</p>
          </div>
          <Button variant="ghost" size="sm" className="text-[#FF6B35] hover:bg-orange-50" onClick={() => navigate('/company/time-records')}>
            Ver todos
            <ArrowUpRight className="w-3.5 h-3.5 ml-1" />
          </Button>
        </div>
        <div className="divide-y divide-gray-100">
          {recentRecords.length === 0 ? (
            <div className="p-8 text-center text-gray-400 text-sm">
              <Clock className="w-8 h-8 mx-auto mb-2 text-gray-200" />
              No hay fichajes recientes
            </div>
          ) : (
            recentRecords.map((record) => (
              <div key={record.id} className="px-5 py-3.5 flex items-center gap-4 hover:bg-gray-50/50 transition-colors">
                <div className="w-9 h-9 bg-orange-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <Clock className="w-4 h-4 text-[#FF6B35]" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{record.user_name || 'Trabajador'}</p>
                  <p className="text-xs text-gray-500">
                    {record.clock_out_time ? 'Entrada y salida' : 'Entrada registrada'}
                    {record.total_hours ? ` — ${record.total_hours}h` : ''}
                  </p>
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">
                  {record.clock_in_time ? new Date(record.clock_in_time).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' }) : ''}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, onClick, accent = 'bg-orange-50 text-[#FF6B35]' }) {
  return (
    <div
      className={`bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all duration-200 ${onClick ? 'cursor-pointer hover:-translate-y-0.5' : ''}`}
      onClick={onClick}
      data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-semibold tracking-widest text-gray-500 uppercase">{label}</span>
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent.split(' ')[0]}`}>
          <Icon className={`w-4 h-4 ${accent.split(' ')[1]}`} />
        </div>
      </div>
      <p className="text-3xl font-bold text-gray-900">{value}</p>
    </div>
  );
}

function QuickAction({ icon: Icon, label, sublabel, onClick, badge, danger }) {
  return (
    <div
      onClick={onClick}
      className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3.5 cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
      data-testid={`quick-action-${label.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${danger ? 'bg-red-50' : 'bg-orange-50'}`}>
        <Icon className={`w-5 h-5 ${danger ? 'text-red-500' : 'text-[#FF6B35]'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{sublabel}</p>
      </div>
      {badge > 0 && (
        <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center text-white ${danger ? 'bg-red-500' : 'bg-[#FF6B35]'}`}>
          {badge}
        </span>
      )}
      <ArrowUpRight className="w-4 h-4 text-gray-300 flex-shrink-0" />
    </div>
  );
}
